// « Connecteurs autorisés » of the user form (Administration → Utilisateurs): Tous / Tous sauf… /
// Seulement… and, for the last two, the checklist of the office's connectors (GET /api/connectors,
// Administration → Connecteurs names): the detected ones, one row per service — the Claude and
// Codex copies of GitHub ticked together —, and the ones added in the office (the library: one row
// each, « ajouté par … · tous les étages »), plus the keys of the policy no longer listed (dashed,
// kept ticked). Each mode keeps its own ticks, so switching back and forth loses nothing;
// only the active mode's are sent. An administrator is never restricted: a note in its place.
// The list may arrive after the form opened (setServers fills it in place); when it cannot be
// loaded, the current setting is shown and kept (the mode is locked).
import { h, input, segmented, statusDot } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { iconForConnector } from '../connectoricons.js';
import { normalizePolicy, policyChoices, policySummary, connectorTone, fold } from './model.js';
import { aid } from './common.js';
import './strings.js';

/** Rows from which the checklist gets a filter box. */
export const POLICY_FILTER_MIN = 9;

/**
 * Creates the control.
 * @param {unknown} policy the user's policy (PublicUser.connectorPolicy; an administrator's reads « Tous »)
 * @param {{role?: string, servers?: object[]|null, failed?: boolean}} [opts] servers: GET /api/connectors
 *   servers (null while loading); failed: the list could not be loaded
 * @returns {{el: HTMLElement, setRole(role: string): void, setServers(servers: object[]|null, opts?: {failed?: boolean}): void,
 *   getValue(): {mode: string, keys: string[]}}}
 */
export function createPolicyControl(policy, { role = 'member', servers = null, failed = false } = {}) {
  const initial = normalizePolicy(policy);
  let mode = initial.mode;
  const sets = { except: new Set(initial.mode === 'except' ? initial.keys : []), only: new Set(initial.mode === 'only' ? initial.keys : []) };
  let list = Array.isArray(servers) ? servers : null;
  let unavailable = !list && failed;
  let query = '';

  const legendId = aid('policy');
  const modeHint = h('p', { class: 'field-hint adm-policy-modehint', 'aria-live': 'polite' });
  const seg = segmented(['all', 'except', 'only'].map((v) => ({ value: v, label: t(`adm.user.conns.${v}`) })), {
    value: mode, label: t('adm.user.conns'),
    onChange: (v) => { mode = v; query = ''; render(); },
  });
  seg.setAttribute('aria-labelledby', legendId);
  seg.classList.add('adm-policy-modes');
  const body = h('div', { class: 'adm-policy-body' });
  const noneWarn = h('p', { class: 'adm-note is-amber adm-policy-none', role: 'status', hidden: true }, t('adm.user.connsOnlyNone'));
  const box = h('fieldset', { class: 'adm-fieldset field adm-policy', dataset: { mode }, 'aria-describedby': `${legendId}-hint` },
    h('legend', { class: 'field-label', id: legendId }, t('adm.user.conns')),
    h('p', { class: 'field-hint', id: `${legendId}-hint` }, t('adm.user.connsHint')),
    seg, modeHint, body, noneWarn,
    h('p', { class: 'field-hint adm-policy-notes' }, t('adm.user.connsNotConcerned'), ' ', t('adm.user.connsKeysHint')));
  const adminNote = h('p', { class: 'adm-note adm-policy-admin', hidden: true }, t('adm.user.connsAdmin'));
  const el = h('div', { class: 'adm-policy-wrap' }, box, adminNote);

  /** The checklist rows of the active mode: the detected services, then the policy's other keys. */
  function rows() {
    return policyChoices(list || [], { mode: 'only', keys: [...sets.except, ...sets.only] });
  }

  function syncNone() {
    noneWarn.hidden = !(mode === 'only' && !unavailable && list && sets.only.size === 0);
  }

  function checkRow(row) {
    const set = sets[mode];
    const on = row.keys.filter((k) => set.has(k));
    const all = on.length === row.keys.length;
    const cb = h('input', {
      type: 'checkbox', class: 'adm-check-input', value: row.id, checked: all, dataset: { keys: row.keys.join(' ') },
      'aria-label': row.label,
    });
    cb.indeterminate = on.length > 0 && !all;
    const meta = row.unknown
      ? [t(row.library ? 'adm.user.connsLibraryGone' : 'adm.user.connsUnknown')]
      : row.library
        ? [row.addedByName ? t('adm.user.connsLibrary', { name: row.addedByName }) : t('adm.user.connsLibraryNoName'), t(`connector.status.${row.status || 'unknown'}`)]
        : [row.engines.map((e) => t(`adm.conn.engine.${e}`)).join(' · '), t(`connector.status.${row.status || 'unknown'}`)];
    if (on.length > 0 && !all) meta.push(t('adm.user.connsPartial', { keys: on.join(', ') }));
    const first = row.servers[0];
    const label = h('label', {
      class: ['adm-check', 'adm-policy-row', { 'is-unknown': row.unknown, 'is-on': all || on.length > 0, 'is-library': row.library }],
      title: row.keys.join(', '), dataset: { id: row.id },
    }, cb,
    iconForConnector(row.library && first ? { key: first.catalogId || first.key, name: row.label } : first || row.keys[0], { size: 26 }),
    h('span', { class: 'adm-check-text' }, row.label, h('span', { class: 'adm-check-meta mono-label' }, meta.filter(Boolean).join(' · '))),
    row.unknown ? null : statusDot(connectorTone(row.status)));
    cb.addEventListener('change', () => {
      // A row stands for every copy of its service: tick or untick them all.
      for (const k of row.keys) { if (cb.checked) set.add(k); else set.delete(k); }
      cb.indeterminate = false;
      label.classList.toggle('is-on', cb.checked);
      syncNone();
    });
    return label;
  }

  function renderList() {
    const all = rows();
    if (!list) {
      body.replaceChildren(h('p', { class: 'adm-note' }, t('adm.user.connsLoading')));
      return;
    }
    if (!all.length) {
      body.replaceChildren(h('p', { class: 'adm-note' }, t('adm.user.connsNone')));
      return;
    }
    const needle = fold(query);
    const hay = (r) => fold(`${r.label} ${r.keys.join(' ')} ${r.addedByName || ''}`);
    const shown = needle ? all.filter((r) => hay(r).includes(needle)) : all;
    const checklist = h('div', { class: 'adm-checklist adm-policy-list' }, shown.map(checkRow));
    const empty = h('p', { class: 'muted adm-policy-nomatch', hidden: shown.length > 0 }, t('adm.user.connsNoMatch'));
    const nodes = [];
    if (all.length >= POLICY_FILTER_MIN) {
      const filter = input({
        type: 'search', class: 'input-sm adm-policy-filter', value: query, placeholder: t('adm.user.connsFilter'),
        'aria-label': t('adm.user.connsFilter'),
        onInput: (e) => {
          query = e.target.value;
          const next = fold(query);
          const kept = next ? all.filter((r) => hay(r).includes(next)) : all;
          checklist.replaceChildren(...kept.map(checkRow));
          empty.hidden = kept.length > 0;
        },
      });
      nodes.push(filter);
    }
    nodes.push(checklist, empty);
    body.replaceChildren(...nodes);
  }

  function render() {
    box.dataset.mode = mode;
    modeHint.textContent = t(`adm.user.connsMode.${mode}`);
    for (const b of seg.childNodes) b.disabled = unavailable;
    if (unavailable) {
      body.hidden = false;
      body.replaceChildren(h('p', { class: 'adm-note is-amber adm-policy-unavailable', role: 'status' },
        t('adm.user.connsUnavailable', { summary: policySummary(initial) })));
    } else if (mode === 'all') {
      body.hidden = true;
      body.replaceChildren();
    } else {
      body.hidden = false;
      renderList();
    }
    syncNone();
  }

  function setRole(r) {
    box.hidden = r === 'admin';
    adminNote.hidden = r !== 'admin';
  }

  render();
  setRole(role);
  return {
    el,
    setRole,
    setServers(next, { failed: nextFailed = false } = {}) {
      if (Array.isArray(next)) {
        list = next;
        // The list came back: the setting can be changed again.
        unavailable = false;
      } else if (!list) {
        unavailable = !!nextFailed;
      }
      render();
    },
    getValue() {
      // The list never came: the setting shown is the user's own, unchanged.
      if (unavailable) return initial;
      return normalizePolicy(mode === 'all' ? { mode } : { mode, keys: [...sets[mode]] });
    },
  };
}
